/**
 *
 * App
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 * For now, it calls the legacy app; routing will be re-worked and moved there
 */

import React from 'react';
import { Navigate } from 'react-router';
import { createBrowserRouter } from 'react-router-dom';

import AppWrapper from '../../legacy/main/app';

import PrivateRoute from '../shared/PrivateRoute/PrivateRoute';
import PermissionedRoute from '../shared/PermissionedRoute/PermissionedRoute';
import OptionalPermissionedRoute from '../shared/PermissionedRoute/OptionalPermissionedRoute';

import LoginPage from '../LoginPage/Loadable';
import LogoutPage from '../LogoutPage/LogoutPage';
import PasswordResetPage from '../PasswordResetPage/Loadable';
import Unauthorized from '../../legacy/main/components/error/unauthorized';

import RevFlow from '../RevFlow/RevFlow';
import StatisticsPage from '../Statistics/StatisticsPage';
import Dashboard from 'legacy/main/components/dashboard/dashboard';
import DashboardV2 from '../DashboardPage/Loadable';
import SellingRateCalendarPage from 'containers/SellingRateCalendarPage/Loadable';

import { FEATURES } from '../../utils/permissions';
import YearView from 'legacy/main/components/yearMonthViews/yearView';
import MonthView from 'legacy/main/components/yearMonthViews/monthView';
import DayView from 'legacy/main/components/dayView/dayView';
import BudgetCalculatorPage from 'containers/BudgetCalculatorPage/Loadable';
import CancellationReportPage from '../CancellationReportPage/Loadable';
import BookingsReportPage from '../BookingsReportPage/Loadable';
import ComparisonReportPage from '../ComparisonReportPage/Loadable';
import SegmentationReportPage from '../SegmentationReportPage/Loadable';
import FiscalBreakdownReportPage from '../FiscalBreakdownReportPage/Loadable';
import PickupReportPageV2 from '../PickupReportPageV2/Loadable';
import PickupReportPageV3 from '../PickupReportPageV3/Loadable';
import CommissionReportPage from '../CommissionReportPage/Loadable';
import SellingRateReportPage from '../SellingRateReportPage/Loadable';
import RecommendationReportPage from '../RecommendationReportPage/Loadable';
import OtaParityPage from '../OtaParityPage/Loadable';
import ApaleoConnectPage from '../ApaleoConnectPage/Loadable';
import SummaryPage from '../FinanceHub/SummaryPage/Loadable';
import MonthlyTotalsPage from '../FinanceHub/MonthlyTotalsPage/Loadable';
import SegmentationPage from '../FinanceHub/SegmentationPage/Loadable';
import GroupDisplacementForm from 'containers/CalculationForms/components/GroupDisplacement/GroupDisplacementForm';
import OccupiedRoomForm from 'containers/CalculationForms/components/OccupiedCostForm/OccupiedRoomForm';
import RateCalculatorForm from 'containers/CalculationForms/components/RateCalculator/RateCalculatorForm';
import RateChangeImpactForm from 'containers/CalculationForms/components/RateChangeImpact/RateChangeImpactForm';
import GroupPickupReportPage from '../GroupPickupReportPage/Loadable';
import CustomisedReportPage from '../CustomisedReportPage/Loadable';
import SettingsCalendarPage from '../settings/SettingsCalendarPage/Loadable';
import GenericPageError from 'components/GenericPageError';
import BulkUpdatePage from 'containers/settings/BulkUpdatePage/Loadable';
import SellingRateMassUpdatePage from 'containers/settings/SellingRateMassUpdatePage/Loadable';
import SegmentBucketManagementPage from 'containers/settings/SegmentBucketManagementPage/Loadable';
import OtaCompSetPage from 'containers/settings/OtaCompSetPage/Loadable';
import ScheduledEmailsPage from 'containers/settings/ScheduledEmailsPage/Loadable';
import RoomManagementPage from 'containers/settings/RoomManagementPage/Loadable';
import EventManagementPage from 'containers/settings/EventManagementPage/Loadable';
import PropertyOrderManagementPage from '../settings/PropertyManagementPage/Loadable';
import AdminRoute from 'containers/shared/AdminRoute/AdminRoute';
import PropertiesManagement from 'containers/admin/PropertiesManagement/Loadable';
import UsersManagement from 'containers/admin/UsersManagement/Loadable';
import BudgetManagement from 'containers/admin/BudgetManagement/Loadable';
import Scheduler from 'containers/admin/Scheduler/Loadable';
import PropertyCreation from 'containers/admin/PropertyCreation/Loadable';
import AccountManagementPage from 'containers/admin/AccountManagement/Loadable';
import BusinessChangePage from 'containers/FinanceHub/BusinessChangePage/Loadable';
import HurdleRackAdvisorPage from 'containers/settings/HurdleRackAdvisorPage/Loadable';
import RateCodeManagementPage from 'containers/settings/RateCodeManagement/Loadable';
import NotificationsPage from 'containers/NotificationsPage/Loadable';
import NotificationCreationPage from 'containers/admin/NotificationCreation/Loadable';
import ComponentRoomManagementPage from 'containers/settings/ComponentRoomManagementPage/Loadable';
import BudgetCreatorPage from 'containers/BudgetCreatorPage/Loadable';

const routes = createBrowserRouter([
    { path: '/login', element: <LoginPage /> },
    { path: '/logout', element: <LogoutPage /> },
    { path: '/resetPassword', element: <PasswordResetPage /> },
    { path: '/resetPassword/:token', element: <PasswordResetPage /> },
    { path: '/unauthorized', element: <Unauthorized /> },
    {
        element: <PrivateRoute />,
        children: [
            {
                element: <AppWrapper />,
                children: [
                    {
                        errorElement: <GenericPageError />,
                        children: [
                            { path: '/app/dashboard', element: <Dashboard /> },
                            { path: '/app/notifications', element: <NotificationsPage /> },
                            { path: '/app/yearly', element: <YearView /> },
                            { path: '/app/monthly', element: <MonthView /> },
                            { path: '/app/daily', element: <DayView /> },
                            { path: '/app/budgetCalculator', element: <BudgetCalculatorPage /> },
                            { path: '/app/budgetBuilder', element: <BudgetCreatorPage /> },
                            {
                                element: <PermissionedRoute requiredFeatures={[FEATURES.dashboardV2]} />,
                                children: [{ path: 'app/dashboard/v2', element: <DashboardV2 /> }],
                            },
                            {
                                element: (
                                    <PermissionedRoute requiredFeatures={[FEATURES.groupDisplacementCalculator]} />
                                ),
                                children: [
                                    { path: '/app/tools/group-displacement', element: <GroupDisplacementForm /> },
                                ],
                            },
                            {
                                element: (
                                    <PermissionedRoute requiredFeatures={[FEATURES.costOfOccupiedRoomCalculator]} />
                                ),
                                children: [{ path: '/app/tools/occupied-room-cost', element: <OccupiedRoomForm /> }],
                            },
                            {
                                element: <PermissionedRoute requiredFeatures={[FEATURES.rateCalculator]} />,
                                children: [{ path: '/app/tools/rate-calculator', element: <RateCalculatorForm /> }],
                            },
                            {
                                element: <PermissionedRoute requiredFeatures={[FEATURES.rateChangeImpact]} />,
                                children: [
                                    { path: '/app/tools/rate-change-impact', element: <RateChangeImpactForm /> },
                                ],
                            },
                            {
                                element: (
                                    <OptionalPermissionedRoute
                                        optionalFeatures={[FEATURES.cardinalRecommendations, FEATURES.viewAERecs]}
                                    />
                                ),
                                children: [{ path: '/app/sellingRateCalendar', element: <SellingRateCalendarPage /> }],
                            },
                            {
                                element: <PermissionedRoute requiredFeatures={[FEATURES.cancellationReport]} />,
                                children: [
                                    { path: '/app/reports/cancellationReport', element: <CancellationReportPage /> },
                                ],
                            },
                            {
                                element: <PermissionedRoute requiredFeatures={[FEATURES.bookingsReport]} />,
                                children: [{ path: '/app/reports/bookingsReport', element: <BookingsReportPage /> }],
                            },
                            {
                                element: <PermissionedRoute requiredFeatures={[FEATURES.comparisonReport]} />,
                                children: [
                                    { path: '/app/reports/comparisonReport', element: <ComparisonReportPage /> },
                                ],
                            },
                            {
                                element: <PermissionedRoute requiredFeatures={[FEATURES.segmentationReport]} />,
                                children: [
                                    { path: '/app/reports/segmentationReport', element: <SegmentationReportPage /> },
                                ],
                            },
                            {
                                element: <PermissionedRoute requiredFeatures={[FEATURES.fiscalBreakdownReport]} />,
                                children: [
                                    {
                                        path: '/app/reports/fiscalBreakdownReport',
                                        element: <FiscalBreakdownReportPage />,
                                    },
                                ],
                            },
                            {
                                element: <PermissionedRoute requiredFeatures={[FEATURES.pickupReportV2]} />,
                                children: [{ path: '/app/reports/pickupReportV2', element: <PickupReportPageV2 /> }],
                            },
                            {
                                element: <PermissionedRoute requiredFeatures={[FEATURES.segmentForecast]} />,
                                children: [{ path: '/app/reports/pickupReportV3', element: <PickupReportPageV3 /> }],
                            },
                            {
                                element: <PermissionedRoute requiredFeatures={[FEATURES.commissionReport]} />,
                                children: [
                                    { path: '/app/reports/commissionReport', element: <CommissionReportPage /> },
                                ],
                            },
                            {
                                element: <PermissionedRoute requiredFeatures={[FEATURES.sellingRateReport]} />,
                                children: [
                                    { path: '/app/reports/sellingRateReport', element: <SellingRateReportPage /> },
                                ],
                            },
                            {
                                element: <PermissionedRoute requiredFeatures={[FEATURES.recommendationReport]} />,
                                children: [
                                    {
                                        path: '/app/reports/recommendationReport',
                                        element: <RecommendationReportPage />,
                                    },
                                ],
                            },
                            {
                                element: <PermissionedRoute requiredFeatures={[FEATURES.otaParity]} />,
                                children: [{ path: '/app/otaParity', element: <OtaParityPage /> }],
                            },
                            {
                                element: <PermissionedRoute requiredFeatures={[FEATURES.apaleoConnect]} />,
                                children: [{ path: '/app/apaleoconnect', element: <ApaleoConnectPage /> }],
                            },
                            {
                                element: (
                                    <PermissionedRoute
                                        requiredFeatures={[FEATURES.financeHub, FEATURES.financeHubBusinessChange]}
                                    />
                                ),
                                children: [{ path: '/app/financehub/businesschange', element: <BusinessChangePage /> }],
                            },
                            {
                                element: (
                                    <PermissionedRoute
                                        requiredFeatures={[FEATURES.financeHub, FEATURES.financeHubSummary]}
                                    />
                                ),
                                children: [{ path: '/app/financehub/summary', element: <SummaryPage /> }],
                            },
                            {
                                element: (
                                    <PermissionedRoute
                                        requiredFeatures={[FEATURES.financeHub, FEATURES.financeHubMonthlyTotals]}
                                    />
                                ),
                                children: [{ path: '/app/financehub/monthlytotals', element: <MonthlyTotalsPage /> }],
                            },
                            {
                                element: (
                                    <PermissionedRoute
                                        requiredFeatures={[FEATURES.financeHub, FEATURES.financeHubSegment]}
                                    />
                                ),
                                children: [{ path: '/app/financehub/segmentation', element: <SegmentationPage /> }],
                            },
                            {
                                element: (
                                    <PermissionedRoute
                                        requiredFeatures={[FEATURES.financeHub, FEATURES.financeHubSegmentV2]}
                                    />
                                ),
                                children: [
                                    {
                                        path: '/app/financehub/segmentationV2',
                                        element: <SegmentationPage version={2} />,
                                    },
                                ],
                            },
                            {
                                element: <PermissionedRoute requiredFeatures={[FEATURES.groupPickupReport]} />,
                                children: [
                                    { path: '/app/reports/groupPickupReport', element: <GroupPickupReportPage /> },
                                ],
                            },
                            {
                                element: <PermissionedRoute requiredFeatures={[FEATURES.customisedReport]} />,
                                children: [{ path: '/app/reports/customised', element: <CustomisedReportPage /> }],
                            },
                            {
                                element: <PermissionedRoute requiredFeatures={[FEATURES.statistics]} />,
                                children: [{ path: '/app/reports/stats', element: <StatisticsPage /> }],
                            },
                            {
                                element: <PermissionedRoute requiredFeatures={[FEATURES.settingsCalendar]} />,
                                children: [
                                    { path: '/settings/calendar', element: <SettingsCalendarPage /> },
                                    { path: '/settings/bulkupdate', element: <BulkUpdatePage /> },
                                ],
                            },
                            {
                                element: <PermissionedRoute requiredFeatures={[FEATURES.sellingRateMassUpdate]} />,
                                children: [
                                    { path: '/settings/sellingRateMassUpdate', element: <SellingRateMassUpdatePage /> },
                                ],
                            },
                            {
                                element: <PermissionedRoute requiredFeatures={[FEATURES.segmentBucketManagement]} />,
                                children: [
                                    { path: '/settings/segmentBuckets', element: <SegmentBucketManagementPage /> },
                                ],
                            },
                            {
                                element: (
                                    <PermissionedRoute
                                        requiredFeatures={[FEATURES.otaInsights, FEATURES.otaCompsets]}
                                    />
                                ),
                                children: [{ path: '/settings/otaCompSets', element: <OtaCompSetPage /> }],
                            },
                            {
                                element: <PermissionedRoute requiredFeatures={[FEATURES.scheduledEmailsManagement]} />,
                                children: [{ path: '/settings/scheduledEmails', element: <ScheduledEmailsPage /> }],
                            },
                            {
                                element: <PermissionedRoute requiredFeatures={[FEATURES.roomManagement]} />,
                                children: [{ path: '/settings/rooms', element: <RoomManagementPage /> }],
                            },
                            {
                                element: <PermissionedRoute requiredFeatures={[FEATURES.propertyManagement]} />,
                                children: [{ path: '/settings/properties', element: <PropertyOrderManagementPage /> }],
                            },
                            {
                                element: <PermissionedRoute requiredFeatures={[FEATURES.events]} />,
                                children: [{ path: '/settings/events', element: <EventManagementPage /> }],
                            },
                            {
                                element: <PermissionedRoute requiredFeatures={[FEATURES.hurdleRackAdvisor]} />,
                                children: [{ path: '/settings/hurdleRackAdvisor', element: <HurdleRackAdvisorPage /> }],
                            },
                            {
                                element: <PermissionedRoute requiredFeatures={[FEATURES.rateCodeManagement]} />,
                                children: [{ path: '/settings/rateCodes', element: <RateCodeManagementPage /> }],
                            },
                            {
                                element: <PermissionedRoute requiredFeatures={[FEATURES.componentRoomsManagement]} />,
                                children: [
                                    { path: '/settings/componentRooms', element: <ComponentRoomManagementPage /> },
                                ],
                            },
                            {
                                element: <PermissionedRoute requiredFeatures={[FEATURES.revflow]} />,
                                children: [{ path: '/app/revflow', element: <RevFlow /> }],
                            },
                            { path: '*', element: <Navigate to="/app/dashboard" replace /> },
                        ],
                    },
                ],
            },
        ],
    },
    {
        element: <AdminRoute />,
        children: [
            {
                element: <AppWrapper />,
                children: [
                    {
                        errorElement: <GenericPageError />,
                        children: [
                            { path: '/admin/properties', element: <PropertiesManagement /> },
                            { path: '/admin/users', element: <UsersManagement /> },
                            { path: '/admin/budget', element: <BudgetManagement /> },
                            { path: '/admin/scheduler', element: <Scheduler /> },
                            { path: '/admin/creation', element: <PropertyCreation /> },
                            { path: '/admin/accounts', element: <AccountManagementPage /> },
                            { path: '/admin/notifications', element: <NotificationCreationPage /> },
                        ],
                    },
                ],
            },
        ],
    },
]);

export default routes;
